import { FormEvent, useEffect, useMemo, useState } from 'react';
import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';
import { getPhoneCallsDashboardUrl } from '../utils/phoneCallsDashboardUrl';

function PhoneCalls() {
  const expectedPassword = process.env.REACT_APP_PHONE_CALLS_PASSWORD?.trim() ?? '';
  const dashboardUrl = useMemo(() => getPhoneCallsDashboardUrl(), []);

  const passwordConfigured = Boolean(expectedPassword);
  const dashboardConfigured = Boolean(dashboardUrl);

  const [unlocked, setUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  useEffect(() => {
    if (!passwordConfigured || !dashboardConfigured) {
      setUnlocked(false);
    }
  }, [passwordConfigured, dashboardConfigured]);

  const showDashboard = unlocked && passwordConfigured && dashboardConfigured;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPasswordError(false);
    if (!passwordConfigured) return;
    if (passwordInput === expectedPassword) {
      setUnlocked(true);
      setPasswordInput('');
    } else {
      setPasswordError(true);
    }
  }

  function handleLock() {
    setUnlocked(false);
    setPasswordInput('');
    setPasswordError(false);
  }

  return (
    <>
      <Helmet>
        <title>Phone Calls</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="description"
          content="Password-protected phone call dashboard for Harpwood House."
        />
        <link rel="canonical" href="https://www.harpwood.house/phone-calls" />
      </Helmet>

      <div className="page">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section phone-calls-page">
            <div className="phone-calls-container">
              <h1>Phone Calls</h1>

              {!passwordConfigured && (
                <div className="phone-calls-config-warning" role="alert">
                  <strong>Configuration required:</strong> set{' '}
                  <code className="phone-calls-config-code">REACT_APP_PHONE_CALLS_PASSWORD</code> in
                  your environment and rebuild the app. The dashboard cannot be unlocked until this is
                  set.
                </div>
              )}

              {!dashboardConfigured && (
                <div className="phone-calls-config-warning" role="alert">
                  <strong>Configuration required:</strong> the Power BI dashboard URL could not be
                  loaded. Set{' '}
                  <code className="phone-calls-config-code">REACT_APP_PHONE_CALLS_DASHBOARD_URL</code>{' '}
                  to override the default Harpwood embed URL and rebuild the app.
                </div>
              )}

              {showDashboard && dashboardUrl ? (
                <div className="phone-calls-dashboard-panel">
                  <div className="phone-calls-dashboard-toolbar">
                    <button type="button" className="phone-calls-lock-btn" onClick={handleLock}>
                      Lock dashboard
                    </button>
                  </div>
                  <div className="phone-calls-iframe-wrap">
                    <iframe
                      title="Harpwood Phone Call Dashboard"
                      src={dashboardUrl}
                      className="phone-calls-iframe"
                      allowFullScreen
                    />
                  </div>
                </div>
              ) : (
                passwordConfigured && (
                  <div className="phone-calls-gate">
                    <p className="phone-calls-intro">
                      Enter the password you were given to view the phone call dashboard.
                    </p>
                    <form className="phone-calls-form" onSubmit={handleSubmit} noValidate>
                      <label htmlFor="phone-calls-password" className="phone-calls-label">
                        Password
                      </label>
                      <input
                        id="phone-calls-password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        className="phone-calls-input"
                        value={passwordInput}
                        onChange={(ev) => {
                          setPasswordInput(ev.target.value);
                          setPasswordError(false);
                        }}
                        disabled={!dashboardConfigured}
                      />
                      {passwordError && (
                        <p className="phone-calls-form-error" role="alert">
                          Incorrect password. Please try again.
                        </p>
                      )}
                      <button
                        type="submit"
                        className="phone-calls-submit"
                        disabled={!dashboardConfigured}
                      >
                        Unlock dashboard
                      </button>
                    </form>
                  </div>
                )
              )}
            </div>
          </section>
        </main>

      <Footer />
      </div>
    </>
  );
}

export default PhoneCalls;
